/**
 * lx-ai-input —— 底部 AI 输入栏
 *
 * 常驻底部，紧贴导航栏上方（对齐手绘草图的「输入命令…」）。
 *
 * 交互（设计文档 3.5）：
 *   · 打字     → 回车发送（Shift+Enter 换行）
 *   · 长按按钮 → 进入**按住说话**模式：按住期间持续识别，松手即结束
 *
 * M5 把语音从「点一下开浮层、再点一次收工」改成了**按住说话**：
 *   语音输入的心智模型天然是「按住这个键开始、松手就停」。
 *   旧设计要用户在识别过程中腾出一只手去点「说完了」，
 *   既别扭，又容易在寻找按钮时错过一句话。现在：
 *     · 按下 → 开始录音（浮层升起，显示声波与实时文字）
 *     · 松手 → 立即 stop()，出最终文本
 *     · 上滑 → 取消（像微信那样）
 *   浮层里同时保留一个「结束」按钮，照顾触屏不灵或想用鼠标的情况。
 *
 * ⚠️ 注意：语音按钮只在**输入框为空**时出现；一旦有字，它变回发送箭头。
 *    这是有意的取舍（底部栏太窄放不下两个按钮），但也意味着
 *    「输入框里已经有字时长按」不会进语音 —— 用户需要先清空。
 *
 * M3：接上**完整闭环**。一句话变成真实的状态变更：
 *   自然语言 → 模型 → 清洗 → 解析 → 执行 → 展示
 *
 * 展示的文案来源严格遵循信任原则（2.1.1）：
 *   · 成功 → 解释器读 successText 本地拼接（这句是真的）
 *   · 失败 → 真实错误回喂 AI 二轮，由 AI 解释（这时它知道结果了）
 *   · 二轮再失败 → 原样展示真实错误（不再让 AI 说话）
 */

import { LxElement, define } from '@/core/base-element';
import { settingsRepo } from '@/settings/settings';
import { handleUserInput } from '@/ai/feedback';
import { registerAllApis } from '@/il/register';
import { AIError } from '@/ai/client';
import { trace } from '@/ai/trace';
import { isVoiceAvailable, voiceUnavailableReason } from '@/ai/voice';
import { pushOverlay, uiStore } from '@/core/ui-store';
import type { LxVoiceView } from './lx-voice-view';
import type { InteractionResult } from '@/ai/feedback';

/**
 * 装配全部能力 —— 越早越好。
 *
 * 之所以放在输入栏这个模块的顶层而不是 main.ts：
 * 输入栏是 AI 链路的入口，它被加载就意味着「即将有人要用指令控制应用」。
 * 放在这里能保证能力在任何用户交互发生前就已就绪，
 * 而 main.ts 不必操心「谁先谁后」这种顺序问题。
 * registerAllApis 自身幂等，重复调用无副作用。
 */
registerAllApis();

/** 多久算「长按」（此时还没开始录音，只是把按钮切成语音态） */
const LONG_PRESS_MS = 260;

/** 长按判定期间手指可容忍的抖动（像素）。超过就认为用户不是想长按 */
const LONG_PRESS_SLOP = 12;

/** 聆听态下上滑多少像素算「取消」 */
const CANCEL_SLIDE_PX = 60;

export class LxAiInput extends LxElement {
  private _longPressTimer: number | null = null;
  /** 是否已进入「按住说话」态（手指按下且长按计时已到） */
  private _holding = false;
  /** 当前正在跟踪的指针 id（多指场景下只认第一根手指） */
  private _pointerId: number | null = null;
  /** 手势起点，用于判断抖动放弃与上滑取消 */
  private _startX = 0;
  private _startY = 0;
  /** 是否已上滑到「取消」区域（松手时据此决定丢弃还是上屏） */
  private _cancelArmed = false;
  /** uiStore 的取消订阅句柄（LxElement 不自动管理，得手动收尾） */
  private _unsubUiStore: (() => void) | null = null;

  /**
   * 请求是否进行中。
   *
   * 为什么不放在这个组件实例上（曾经的 `_busy` 字段）：
   *   切页时 lx-shell 会重渲染整棵子树（`innerHTML = render()`），
   *   lx-ai-input 被**重建**成新实例 —— 实例字段随之归零，
   *   转圈图标立刻变回默认样式，哪怕后端请求还在飞。
   *   用户看到的是「我刚发的指令好像丢了」。
   *
   * 所以这个状态必须活在**比组件实例更长命**的地方 —— uiStore。
   * uiStore 是内存态单例，切页、重渲染都不影响它。
   */
  private get _busy(): boolean {
    return uiStore.get().busy;
  }

  private set _busy(v: boolean) {
    uiStore.set({ busy: v });
  }

  protected styles(): string {
    return `
      :host {
        display: block;
        background: var(--c-surface);
        border-top: 1px solid var(--c-border);
      }

      .bar {
        display: flex;
        align-items: flex-end;
        gap: var(--sp-2);
        padding: var(--sp-2) var(--layout-content-pad-x);
        min-height: var(--layout-ai-input-h);
      }

      .field {
        flex: 1;
        position: relative;
      }

      textarea {
        display: block;
        width: 100%;
        /* 最多 4 行，超出内部滚动 */
        min-height: 40px;
        max-height: 104px;
        padding: var(--sp-2) var(--sp-3);
        border: 1px solid var(--c-border);
        border-radius: var(--radius-lg);
        background: var(--c-surface-2);
        color: var(--c-fg);
        font-size: var(--fs-md);
        line-height: var(--lh-base);
        resize: none;
        overflow-y: auto;
        transition: border-color var(--dur-fast) var(--ease-standard);
      }

      textarea::placeholder {
        color: var(--c-fg-subtle);
      }

      textarea:focus {
        outline: none;
        border-color: var(--c-accent);
      }

      textarea:disabled {
        opacity: 0.6;
      }

      /* ---------- 按住说话提示（按住期间替换输入框） ---------- */
      .push-to-talk {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--sp-2);
        min-height: 40px;
        padding: var(--sp-2) var(--sp-3);
        border: 1px solid var(--c-primary);
        border-radius: var(--radius-lg);
        background: var(--c-surface-2);
        color: var(--c-primary);
        font-size: var(--fs-md);
      }

      .push-to-talk .wave {
        display: flex;
        align-items: center;
        gap: 2px;
        height: 16px;
      }

      .push-to-talk .wave i {
        display: block;
        width: 3px;
        border-radius: 2px;
        background: currentColor;
        animation: wave 0.9s ease-in-out infinite;
      }

      .push-to-talk .wave i:nth-child(1) { height: 6px;  animation-delay: 0s; }
      .push-to-talk .wave i:nth-child(2) { height: 14px; animation-delay: .15s; }
      .push-to-talk .wave i:nth-child(3) { height: 9px;  animation-delay: .3s; }
      .push-to-talk .wave i:nth-child(4) { height: 16px; animation-delay: .45s; }

      @keyframes wave {
        0%, 100% { transform: scaleY(.45); opacity: .6; }
        50%      { transform: scaleY(1);   opacity: 1; }
      }

      .send {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        display: grid;
        place-items: center;
        border-radius: var(--radius-full);
        background: var(--c-primary);
        color: var(--c-primary-fg);
        transition: opacity var(--dur-fast) var(--ease-standard);
      }

      /* 输入框为空时，发送键变成麦克风（按住说话） */
      .send.is-voice {
        background: var(--c-surface-2);
        color: var(--c-fg-muted);
        border: 1px solid var(--c-border);
      }

      /* 正在按住 —— 按钮放大 + 主色，给出明确反馈 */
      .send.is-held {
        background: var(--c-primary);
        color: var(--c-primary-fg);
        border-color: transparent;
        transform: scale(1.08);
      }

      .send:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      .send svg {
        width: 20px;
        height: 20px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.8;
      }

      /* ---------- 调试模式的 IL 直连入口 ----------
         只在调试模式开启时出现（见 render）。刻意做成"描边幽灵按钮"
         而不是实心主按钮 —— 它是排障用的后门，不该抢发送键的视觉重心。 */
      .il {
        flex-shrink: 0;
        height: 40px;
        padding: 0 var(--sp-3);
        display: grid;
        place-items: center;
        border-radius: var(--radius-lg);
        border: 1px dashed var(--c-border-strong);
        color: var(--c-fg-muted);
        font-family: var(--font-mono);
        font-size: var(--fs-xs);
        font-weight: var(--fw-medium);
        letter-spacing: 0.04em;
        transition: all var(--dur-fast) var(--ease-standard);
      }

      .il:hover {
        color: var(--c-primary);
        border-color: var(--c-primary);
      }

      /* 请求中：按钮位置换成转圈 */
      .send .spinner {
        width: 18px;
        height: 18px;
        border: 2px solid currentColor;
        border-top-color: transparent;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      /* 未配置 AI 时的引导条 */
      .notice {
        display: flex;
        align-items: center;
        gap: var(--sp-2);
        padding: var(--sp-2) var(--layout-content-pad-x);
        background: var(--c-warning-soft);
        color: var(--c-warning);
        font-size: var(--fs-xs);
      }

      .notice button {
        text-decoration: underline;
        color: inherit;
        font-weight: var(--fw-medium);
      }

      /* 长按语音提示浮层 */
      .voice-hint {
        padding: var(--sp-2) var(--layout-content-pad-x);
        font-size: var(--fs-xs);
        color: var(--c-fg-muted);
        text-align: center;
      }

      @media (prefers-reduced-motion: reduce) {
        .push-to-talk .wave i { animation: none; }
        .send.is-held { transform: none; }
      }
    `;
  }

  protected render(): string {
    const aiReady = settingsRepo.isAIReady();
    const debug = settingsRepo.getApp().debugMode;
    return `
      ${aiReady || debug ? '' : this.renderNotice()}
      <div class="bar">
        ${debug ? this.renderILButton() : ''}
        <div class="field">
          ${
            this._holding
              ? `<div class="push-to-talk" aria-live="polite">
                   <span class="wave"><i></i><i></i><i></i><i></i></span>
                   <span>正在聆听…松手结束</span>
                 </div>`
              : `<textarea
                   rows="1"
                   placeholder="${this._busy ? '正在处理…' : '输入命令…'}"
                   aria-label="向灵讯下达指令"
                   enterkeyhint="send"
                   ${this._busy ? 'disabled' : ''}
                 ></textarea>`
          }
        </div>
        ${this.renderSendButton()}
      </div>
    `;
  }

  /**
   * 发送键与麦克风键是**同一个按钮**，按当前状态变脸：
   *   · 输入框有内容     → 发送箭头
   *   · 输入框为空 + 可用 → 麦克风（按住说话）
   *   · 请求进行中       → 转圈
   *
   * 为什么合并成一个：底部只有一条 56px 的窄栏，再塞一个按钮会让
   * 输入框窄到只能显示几个字。而「有字时发送、空着时说话」这个映射
   * 用户一眼就懂 —— 也是主流 IM 的通行做法。
   */
  private renderSendButton(): string {
    if (this._busy) {
      return `<button class="send" type="button" disabled aria-label="正在处理">
        <span class="spinner"></span>
      </button>`;
    }

    const ta = this.$('textarea') as HTMLTextAreaElement | null;
    const hasText = (ta?.value ?? '').trim().length > 0;
    const voiceOk = isVoiceAvailable();

    if (!hasText && voiceOk) {
      return `<button class="send is-voice ${this._holding ? 'is-held' : ''}"
        type="button" data-voice aria-label="按住说话"
        aria-pressed="${this._holding}">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="9" y="3" width="6" height="11" rx="3"/>
          <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" stroke-linecap="round"/>
        </svg>
      </button>`;
    }

    return `<button class="send" type="button" data-send aria-label="发送">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4.5 12h14M12.5 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>`;
  }

  /** 调试模式下才渲染的 IL 直连按钮 */
  private renderILButton(): string {
    return `<button class="il" type="button" data-open-debug aria-label="打开调试页">IL</button>`;
  }

  private renderNotice(): string {
    return `
      <div class="notice">
        <span>还没配置 AI 服务，指令无法翻译</span>
        <button type="button" data-go-settings>去设置</button>
      </div>
    `;
  }

  protected onMounted(): void {
    const ta = this.$('textarea') as HTMLTextAreaElement | null;
    if (!ta) {
      // 按住说话态下 textarea 不存在，此时只需挂语音按钮的事件
      this.bindVoiceButton();
      return;
    }

    /* ---------- 自动增高：最多 4 行后内部滚动 ---------- */
    const autoGrow = (): void => {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 104)}px`;
      // 发送键的形态取决于「有没有字」—— 有字是箭头，空着是麦克风。
      // 所以文字一变就得重渲染按钮，不能只改 disabled。
      this.updateSendButton();
    };

    ta.addEventListener('input', autoGrow);

    /* ---------- 发送 ---------- */
    const submit = (): void => {
      const text = ta.value.trim();
      if (!text || this._busy) return;

      if (!settingsRepo.isAIReady()) {
        this.emit('ui:modal-open', {
          level: 'error',
          text: '还没有配置 AI 服务。请先在「设置 → AI 服务」里填写服务地址、API Key 和模型名。',
        });
        return;
      }

      ta.value = '';
      autoGrow();
      void this.run(text);
    };

    ta.addEventListener('keydown', (e) => {
      // 回车发送，Shift+Enter 换行。输入法组字期间不响应
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        submit();
      }
    });

    this.$('[data-send]')?.addEventListener('click', submit);

    /* ---------- 未配置 AI 的引导 ---------- */
    this.$('[data-go-settings]')?.addEventListener('click', () => {
      this.emit('ui:tab-changed', { tab: 'settings' });
    });

    /* ---------- 调试模式的 IL 直连入口 ---------- */
    this.$('[data-open-debug]')?.addEventListener('click', () => {
      pushOverlay({ kind: 'debug', refId: '' });
    });

    this.bindVoiceButton();
    autoGrow();
  }

  /**
   * 只重渲染发送/麦克风按钮，不动 textarea。
   *
   * 为什么不直接 this.update()：update() 会重建整棵 shadow DOM，
   * textarea 被替换掉 → 焦点丢失、光标位置丢失、中文输入法组字被打断。
   * 每敲一个字都触发一次这种重建，输入体验会直接崩掉。
   * 所以这里只替换按钮那一个节点。
   */
  private updateSendButton(): void {
    const old = this.$('.send');
    if (!old) return;
    const wasVoice = old.classList.contains('is-voice');
    const shouldBeVoice = !this._busy && !this.hasText() && isVoiceAvailable();
    // 形态没变就不动它，避免无谓的 DOM 抖动
    if (wasVoice === shouldBeVoice && !this._busy) return;

    old.outerHTML = this.renderSendButton();
    this.bindSendButton();
  }

  private hasText(): boolean {
    const ta = this.$('textarea') as HTMLTextAreaElement | null;
    return (ta?.value ?? '').trim().length > 0;
  }

  private bindSendButton(): void {
    // 事件在每个分支里各自绑一次（节点是新建的，不会重复）
    this.$('[data-send]')?.addEventListener('click', () => {
      const ta = this.$('textarea') as HTMLTextAreaElement | null;
      const text = ta?.value.trim() ?? '';
      if (!text || this._busy) return;
      if (!settingsRepo.isAIReady()) {
        this.emit('ui:modal-open', {
          level: 'error',
          text: '还没有配置 AI 服务。请先在「设置 → AI 服务」里填写服务地址、API Key 和模型名。',
        });
        return;
      }
      if (ta) ta.value = '';
      this.update();
      void this.run(text);
    });
    this.bindVoiceButton();
  }

  /**
   * 按住说话。
   *
   * 生命周期：
   *   pointerdown  → 计时 260ms（这么短是为了"按下即有反应"的观感，
   *                  又足够滤掉滚动/误触）。到了就进入 _holding 态并开录。
   *   pointerup    → 立刻 stop()，拿最终文本
   *   pointercancel / pointerleave → 视为取消（比如来电、系统手势打断）
   *
   * 为什么要 pointerleave 也取消：手指按住后往上滑出按钮，
   * 用户表达的通常是「我录错了，不想要了」。继续录会让人困惑。
   */
  private bindVoiceButton(): void {
    const btn = this.$('[data-voice]') as HTMLElement | null;
    if (!btn) return;

    // 只绑按钮上的「按下」。
    //
    // ⚠️ 为什么 pointerup / pointermove **不**绑在按钮上：
    //   按下 260ms 后会进 beginHold()，而它要 this.update() 来把输入框
    //   换成「正在聆听」条 —— 这一渲染会**重建整棵 shadow DOM**，
    //   按钮节点被替换成一个全新的 DOM 节点。
    //   于是「松手」事件再也到不了那个已经脱离文档的旧节点上，
    //   录音就永远停不下来。真机上表现为「长按后卡死」。
    //
    //   解法：把移动与松手交给**比按钮长命**的 window。
    //   window 在一次会话里始终存在，DOM 怎么重建都不影响它收事件。
    btn.addEventListener('pointerdown', this._onVoiceDown);
    btn.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /** 按下：起长按计时（绑在按钮上，因为只有按钮能发起这个手势） */
  private _onVoiceDown = (e: PointerEvent): void => {
    // 只响应主键 / 单指
    if (e.button !== 0) return;
    e.preventDefault();
    this._clearLongPress();

    if (!isVoiceAvailable()) {
      this.emit('ui:modal-open', {
        level: 'info',
        title: '语音输入不可用',
        text: voiceUnavailableReason(),
      });
      return;
    }

    this._startX = e.clientX;
    this._startY = e.clientY;
    this._pointerId = e.pointerId;

    this._longPressTimer = window.setTimeout(() => {
      this._longPressTimer = null;
      void this.beginHold();
    }, LONG_PRESS_MS);
  };

  /** 移动：未进入聆听前判抖动放弃；进入后判上滑取消 */
  private _onVoiceMove = (e: PointerEvent): void => {
    if (this._pointerId === null || e.pointerId !== this._pointerId) return;

    if (!this._holding) {
      const dx = e.clientX - this._startX;
      const dy = e.clientY - this._startY;
      // 还没到长按时间就跑掉了 → 用户多半是想滚动或滑走，放弃这次长按
      if (Math.hypot(dx, dy) > LONG_PRESS_SLOP) this._clearLongPress();
      return;
    }
    // 已在聆听态：上滑超过阈值视为「取消」，与微信一致
    this._cancelArmed = e.clientY < this._startY - CANCEL_SLIDE_PX;
  };

  /** 松手 / 指针被系统收回：统一走收尾 */
  private _onVoiceUp = (e: PointerEvent): void => {
    if (this._pointerId === null || e.pointerId !== this._pointerId) return;
    this._finishVoice(false);
  };

  private _onVoiceCancel = (e: PointerEvent): void => {
    if (this._pointerId === null || e.pointerId !== this._pointerId) return;
    this._finishVoice(true);
  };

  private _finishVoice(abandon: boolean): void {
    this._clearLongPress();
    this._pointerId = null;
    if (this._holding) void this.endHold(abandon);
  }

  private _voiceView: LxVoiceView | null = null;

  /** 进入按住说话态：升起浮层并开始录音 */
  private async beginHold(): Promise<void> {
    this._holding = true;
    this.update();

    pushOverlay({ kind: 'voice', refId: '' });

    // 等浮层挂载。
    //
    // ⚠️ 这里为什么不能直接 document.querySelector('lx-voice-view')：
    //   覆盖层是由 lx-shell 渲染的，而 lx-shell 自己有 shadow root ——
    //   浮层节点长在 shell 的影子树里，document.querySelector **根本看不到**。
    //   早先就是这么写的，于是 view 恒为 null，beginHold 立刻自我复位，
    //   表现为「长按闪一下就没反应了」。
    //
    // 等两帧而不是一帧：pushOverlay → uiStore 通知 → shell.update() 排在
    //   微任务里，而 shadow DOM 真正完成插入要等这次渲染收尾。
    //   一帧（requestAnimationFrame）在部分情况下仍会抢在插入之前，
    //   所以这里多等一帧，稳一点。
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

    const view = this._findVoiceView();
    this._voiceView = view;

    if (!view) {
      // 理论上不该走到这里（浮层由 shell 同步渲染）。真走到了说明
      // 覆盖层机制坏了，那就如实复位，不要留下一个「永远在聆听」的假状态。
      this._holding = false;
      this.update();
      return;
    }

    view.onResult = (text: string) => {
      const ta = this.$('textarea') as HTMLTextAreaElement | null;
      if (ta) {
        ta.value = text;
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(ta.scrollHeight, 104)}px`;
        ta.focus();
      }
    };

    // 浮层内部点「结束」按钮 → 等同于松手
    view.onRequestFinish = () => void this.endHold(false);
  }

  /**
   * 找到语音浮层实例。
   *
   * 必须穿透 shadow 边界 —— 覆盖层长在 lx-shell 的影子树里。
   * 用递归遍历 + `:scope` 语义自己实现，而不是引入全局 deepQuery 工具：
   * 这个查询目前只有这一处需要，就地解决比往 core 里塞一个通用件更划算。
   */
  private _findVoiceView(): LxVoiceView | null {
    const walk = (root: ParentNode): LxVoiceView | null => {
      const hit = root.querySelector('lx-voice-view') as LxVoiceView | null;
      if (hit) return hit;
      const all = root.querySelectorAll('*');
      for (const el of Array.from(all)) {
        const sr = (el as HTMLElement).shadowRoot;
        if (sr) {
          const found = walk(sr);
          if (found) return found;
        }
      }
      return null;
    };
    return walk(document);
  }

  /**
   * 结束按住说话。
   *
   * @param abandon true 表示取消（丢弃结果），false 表示正常结束（上屏文本）
   *
   * 注意浮层自己会调用 session.stop()/cancel() 并在结束后 popOverlay，
   * 这里只负责把 _holding 状态复位、把按钮变回去。
   *
   * 上滑取消（_cancelArmed）在这里统一裁决：调用方不必关心手势细节，
   * 只管说「松手了」，语义上的取消由本方法根据手势状态决定。
   */
  private async endHold(abandon: boolean): Promise<void> {
    if (!this._holding) return;
    this._holding = false;

    const cancelled = abandon || this._cancelArmed;
    this._cancelArmed = false;

    const view = this._voiceView;
    this._voiceView = null;

    if (view) {
      // 让浮层去收尾（它会 stop/cancel 并关闭自己）
      await view.finishFromOutside(cancelled).catch(() => {});
    }

    this.update();
    // 语音结束、输入框有内容了 —— 发送键该变回箭头
    this.updateSendButton();
  }

  connectedCallback(): void {
    super.connectedCallback();

    // 设置页改完 AI 配置后，「未配置」提示条要立刻消失
    this.subscribe('ai:config-changed', () => this.update());

    // 记住上次填的内容：请求期间用户可能切页，回来时输入框还是空的
    // 说明：控制台的模型原文输出不在这里做 —— 那归 trace 管，
    // 由 feedback.ts 在 AI 层按轮次分组输出（见 ai/trace.ts）。
    // 调试页的调用日志订阅 ai:response 事件，与本组件的职责无关。

    // busy 住在 uiStore 里（见 _busy getter 的说明），它变了我得重渲染按钮。
    // 订阅它有两个作用：
    //   ① 本组件自己 start/finish 请求时（this._busy = …）自动刷新；
    //   ② 更重要的 —— 切页让 lx-shell 重建本组件时，新实例挂载后
    //      立即读到 uiStore.busy 仍为 true，于是转圈图标被正确画出来。
    //      这就是「图标跨页面保持一致」的实现方式。
    // uiStore.subscribe 不走 LxElement 那套自动解绑（那只管事件总线），
    // 所以取消订阅函数要自己收好，卸载时手动调用 —— 否则旧实例
    // 会永远挂在监听器集合里，每次 busy 变化都白跑一次 update()。
    this._unsubUiStore = uiStore.subscribe((state, prev) => {
      if (state.busy !== prev.busy) this.update();
    });

    // ── 语音手势的「移动/松手」监听挂在 window 上，且只挂一次 ──
    //
    // 为什么不挂在按钮上：按下后 beginHold() 会重渲染整棵 shadow DOM，
    // 按钮节点被替换，松手事件就再也到不了旧节点 —— 录音卡死。
    // 为什么必须只挂一次：bindVoiceButton() 每次渲染都会跑，
    // 若在里面 addEventListener(window, …)，渲染 N 次就叠 N 组监听，
    // 每组都持有各自版本的闭包状态，松手时互相打架。
    window.addEventListener('pointermove', this._onVoiceMove);
    window.addEventListener('pointerup', this._onVoiceUp);
    window.addEventListener('pointercancel', this._onVoiceCancel);
  }

  /**
   * 完整闭环：自然语言 → 模型 → 清洗 → 解析 → 执行 → 展示。
   *
   * 注意这里**没有**任何「成功/失败」的判断逻辑 —— 那些都在 feedback.ts 里。
   * 输入栏只负责：拿用户的话 → 交给处理链路 → 展示拿回来的文字。
   * 这样职责清晰：文案怎么来的，不归 UI 管。
   */
  private async run(userInput: string): Promise<void> {
    this._busy = true;
    this.update();
    this.emit('ai:request-start', { input: userInput });
    // 轮次分组由 handleUserInput 内部打开（它才知道有几轮、各走哪条路径），
    // 这里只留一行「谁在什么时候发起了这次交互」的锚点。
    trace.note(`⚡ 开始处理（${new Date().toLocaleTimeString()}）`);

    try {
      const result = await handleUserInput(userInput, {
        // 二次确认：把确认文案交给模态框，用户的决定回传给求值器。
        // 这是 Promise 化的 —— 求值器会 await 到用户做出选择为止。
        // onConfirm 与 onCancel 必须成对提供，否则用户点「取消」时
        // 这个 Promise 会永远挂着（界面上看不出问题，但请求永不结束）。
        onConfirm: (text) =>
          new Promise<boolean>((resolve) => {
            let settled = false;
            const done = (v: boolean): void => {
              if (settled) return;
              settled = true;
              resolve(v);
            };
            this.emit('ui:modal-open', {
              level: 'confirm',
              title: '需要确认',
              text,
              onConfirm: () => done(true),
              onCancel: () => done(false),
            });
          }),
      });

      this.emit('ai:request-end', { ok: true });
      this.showResult(result);
    } catch (err) {
      this.emit('ai:request-end', {
        ok: false,
        error: err instanceof Error ? err.message : String(err),
      });

      const reason = err instanceof AIError ? err.reason : String(err);
      const head = err instanceof AIError ? err.message : '出错了';
      trace.note(`✗ 交互整体失败：${head} —— ${reason}`);
      this.emit('ui:modal-open', {
        level: 'error',
        title: '调用失败',
        text: `${head}\n\n${reason}`,
      });
    } finally {
      this._busy = false;
      this.update();
    }
  }

  /** 展示执行结果。文案已由 feedback.ts 决定，这里只负责呈现与分档 */
  private showResult(result: InteractionResult): void {
    const failed = result.results.filter((r) => !r.ok).length;

    // 去重 + 去噪：
    //   ① 回答轮 / 失败二轮里，"AI 说的话"本身就是 explain 那条的 desc，
    //      原样带着 results 渲染会让同一句话出现两遍 → 剔掉 desc 与文案重复的。
    //   ② [查] 方法的 successText 是机械回执（「已读取联系人列表」）。
    //      查询成功时真正的答案在 result.text 里，回执列出来纯属噪音 → 也剔掉。
    //      注意这只是"不展示"，结果**本身**仍保留在 InteractionResult 里供调试页查看。
    const text = result.text.trim();
    const results = result.results.filter((r) => {
      if (!r.ok) return true; // 失败条目一定要留着，那是信任原则的核心
      if (r.descIsReceipt) return false;
      const d = (r.desc ?? '').trim();
      return !(d && text.includes(d));
    });

    // 有失败 → 用 error 级别，让用户一眼知道出了问题（信任原则的外观体现）
    if (failed > 0) {
      this.emit('ui:modal-open', {
        level: 'error',
        title: '执行失败',
        text: result.text,
        results,
      });
      return;
    }

    this.emit('ui:modal-open', {
      level: 'info',
      title: '完成',
      text: result.text,
      results,
    });
  }

  private _clearLongPress(): void {
    if (this._longPressTimer !== null) {
      clearTimeout(this._longPressTimer);
      this._longPressTimer = null;
    }
  }

  protected onUnmounted(): void {
    this._clearLongPress();
    this._pointerId = null;
    window.removeEventListener('pointermove', this._onVoiceMove);
    window.removeEventListener('pointerup', this._onVoiceUp);
    window.removeEventListener('pointercancel', this._onVoiceCancel);
    this._unsubUiStore?.();
    this._unsubUiStore = null;
  }
}

define('lx-ai-input', LxAiInput);
